import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ServiceArea = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const primaryAreas = [
    'Poughkeepsie', 'Kingston', 'Newburgh', 'Middletown', 'Beacon', 'Fishkill',
    'Wappingers Falls', 'Rhinebeck', 'Hyde Park', 'Red Hook', 'Saugerties'
  ];

  const additionalAreas = [
    'Hudson', 'Catskill', 'Coxsackie', 'Athens', 'Kinderhook', 'Ghent',
    'Chatham', 'Millerton', 'Pine Plains', 'NorthEast', 'Rhinecliff', 'West Park'
  ];

  return (
    <section id="service-area" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Serving the Entire{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Hudson Valley
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Local experts who know your area and have established relationships with regional providers
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Primary Service Areas */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiIcons.FiMapPin} className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">Primary Service Areas</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {primaryAreas.map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Additional Areas */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiIcons.FiMap} className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">Additional Coverage</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {additionalAreas.map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Coverage Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">23+</div>
            <div className="text-gray-600">Cities & Towns</div>
          </div>
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
            <div className="text-gray-600">Local Providers</div>
          </div>
          <div className="text-center bg-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
            <div className="text-gray-600">Hudson Valley Coverage</div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Don't See Your Town Listed?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Contact us to check availability in your specific location
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Check Your Location
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceArea;